import ReactGA, { ReactGAImplementation } from "react-ga4"
import { GOOGLE_ANALYTICS_TRACKING_ID, GA4_MEASUREMENT_ID } from "../Config"
import { EventArgs } from "models/GoogleAnalytics"
import { interceptPii } from "./GoogleAnalyticsUtils"

let initialised = false
const queue: EventArgs[] = []
// Capture the URL at queue time so a pre-consent flush uses the real per-page URL, not the flush-time one.
type QueuedPageView = { path: string; location: string }
const pageViews: QueuedPageView[] = []

// Global authentication status cache
let cachedAuthenticationStatus = "Logged Out"

/**
 * Updates the cached authentication status
 * Called by the session system when auth state changes
 */
export const updateAuthenticationStatus = (sessionData: any): void => {
  cachedAuthenticationStatus = sessionData ? "Logged In" : "Logged Out"
}

/**
 * We extend the main `react-ga4` class to implement the `user_properties` scope.
 */
class CustomGAImplementation extends ReactGAImplementation {
  user = (fieldsObject: Record<string, string>) => {
    if (!fieldsObject) {
      console.warn("`fieldsObject` is required in .set()")

      return
    }

    if (typeof fieldsObject !== "object") {
      console.warn("Expected `fieldsObject` arg to be an Object")

      return
    }

    if (Object.keys(fieldsObject).length === 0) {
      console.warn("empty `fieldsObject` given to .set()")
    }

    this._gtag("set", "user_properties", fieldsObject)
  }
}

export const CustomGA = interceptPii(new CustomGAImplementation())

/**
 * Gets the current authentication status from cache
 * No longer attempts to access HttpOnly cookies
 */
const getAuthenticationStatus = (): string => {
  return cachedAuthenticationStatus
}

export const initialiseGA = () => {
  if (!GOOGLE_ANALYTICS_TRACKING_ID || !GA4_MEASUREMENT_ID) {
    throw new Error(
      "GoogleAnalytics: GOOGLE_ANALYTICS_TRACKING_ID and GA4_MEASUREMENT_ID must be set"
    )
  }
  ReactGA.initialize([
    { trackingId: GOOGLE_ANALYTICS_TRACKING_ID, gtagOptions: { send_page_view: false } },
    { trackingId: GA4_MEASUREMENT_ID, gtagOptions: { send_page_view: false } },
  ])
  initialised = true
  // Send any events that were called before GA was properly initialised.
  // TODO: this is a bit of a hack, this should probably go in React state
  queue.forEach((args) => Event(args.action, args))
  pageViews.forEach(sendPageView)
}

export const LogGaEvent = (args: EventArgs) => {
  // Auto-inject authentication status for all events
  const enhancedArgs = {
    ...args,
    authentication_status: getAuthenticationStatus(),
  }

  // For now, duplicate GA4 custom parameters as UA custom dimensions
  if ("field_value" in enhancedArgs) {
    if (typeof enhancedArgs.field_value === "object") {
      enhancedArgs.field_value = JSON.stringify(enhancedArgs.field_value)
    }
    enhancedArgs.dimension7 = enhancedArgs.field_value
  }
  if ("mortgage_type" in enhancedArgs) enhancedArgs.dimension8 = enhancedArgs.mortgage_type
  if ("user_stage" in enhancedArgs) enhancedArgs.dimension9 = enhancedArgs.user_stage
  if ("traffic_light_definition" in enhancedArgs)
    enhancedArgs.dimension10 = enhancedArgs.traffic_light_definition
  if ("full_page_url" in enhancedArgs) enhancedArgs.dimension12 = enhancedArgs.full_page_url
  if ("ltv_band" in enhancedArgs) enhancedArgs.dimension13 = enhancedArgs.ltv_band
  if ("internal_user" in enhancedArgs) enhancedArgs.dimension14 = enhancedArgs.internal_user
  if ("appointment_type" in enhancedArgs) enhancedArgs.dimension15 = enhancedArgs.appointment_type
  if ("eligibility_tier" in enhancedArgs) enhancedArgs.dimension16 = enhancedArgs.eligibility_tier
  if ("total_documents_uploaded" in enhancedArgs)
    enhancedArgs.dimension17 = enhancedArgs.total_documents_uploaded
  if ("product_comparison_status" in enhancedArgs)
    enhancedArgs.dimension18 = enhancedArgs.product_comparison_status
  if ("mortgage_coach_status" in enhancedArgs)
    enhancedArgs.dimension19 = enhancedArgs.mortgage_coach_status
  if ("friendlyId" in enhancedArgs) enhancedArgs.dimension20 = enhancedArgs.friendlyId
  if ("lender_name" in enhancedArgs) enhancedArgs.dimension21 = enhancedArgs.lender_name
  if ("product_name" in enhancedArgs) enhancedArgs.dimension22 = enhancedArgs.product_name
  if ("monthly_cost" in enhancedArgs) enhancedArgs.dimension23 = enhancedArgs.monthly_cost
  if ("initial_rate" in enhancedArgs) enhancedArgs.dimension24 = enhancedArgs.initial_rate
  if ("mortgage_product_type" in enhancedArgs)
    enhancedArgs.dimension25 = enhancedArgs.mortgage_product_type
  if ("initial_period_end_date" in enhancedArgs)
    enhancedArgs.dimension26 = enhancedArgs.initial_period_end_date
  if ("initial_fixed_period" in enhancedArgs)
    enhancedArgs.dimension27 = enhancedArgs.initial_fixed_period
  if ("arrangement_fee" in enhancedArgs) enhancedArgs.dimension28 = enhancedArgs.arrangement_fee
  if ("mortgage_payment_type" in enhancedArgs)
    enhancedArgs.dimension29 = enhancedArgs.mortgage_payment_type
  if ("offset" in enhancedArgs) enhancedArgs.dimension30 = enhancedArgs.offset
  if ("portable" in enhancedArgs) enhancedArgs.dimension31 = enhancedArgs.portable
  if ("overpayments_allowed" in enhancedArgs)
    enhancedArgs.dimension32 = enhancedArgs.overpayments_allowed
  if ("free_legals" in enhancedArgs) enhancedArgs.dimension33 = enhancedArgs.free_legals
  if ("free_valuation" in enhancedArgs) enhancedArgs.dimension34 = enhancedArgs.free_valuation
  if ("cashback" in enhancedArgs) enhancedArgs.dimension35 = enhancedArgs.cashback

  if (!initialised) {
    queue.push(enhancedArgs)
    return
  }
  Event(enhancedArgs.action, enhancedArgs)
}

/**
 * Send an event to the analytics
 * @param eventName The name of the event
 * @param params An object of any parameters
 */
const Event = (eventName: string, params: object) => {
  CustomGA.event(eventName, params)
}

const sendPageView = ({ path, location }: QueuedPageView) => {
  CustomGA.set({ page_location: location, full_page_url: location })
  CustomGA.send({ hitType: "pageview", page: path })
}

export const PageView = (path: string) => {
  // Pure client SPA: window is always defined.
  const location = window.location.href
  if (!initialised) {
    pageViews.push({ path, location })
    return
  }
  sendPageView({ path, location })
}

export function SetGaDimensions(args: Omit<EventArgs, "action" | "event_category">) {
  // For now, duplicate GA4 custom parameters as UA custom dimensions
  if ("field_value" in args) args.dimension7 = args.field_value
  if ("mortgage_type" in args) args.dimension8 = args.mortgage_type
  if ("user_stage" in args) args.dimension9 = args.user_stage
  if ("traffic_light_definition" in args) args.dimension10 = args.traffic_light_definition
  if ("ltv_band" in args) args.dimension13 = args.ltv_band
  if ("appointment_type" in args) args.dimension15 = args.appointment_type
  if ("eligibility_tier" in args) args.dimension16 = args.eligibility_tier
  if ("product_comparison_status" in args) args.dimension18 = args.product_comparison_status
  if ("mortgage_coach_status" in args) args.dimension19 = args.mortgage_coach_status

  ReactGA.set(args)
}
